import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { workflowAPI } from '../services/api';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(10px);
`;

const ModalContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #333;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1rem 0;
`;

const ModalSubtitle = styled.p`
  color: #666;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  white-space: pre-wrap;
  word-wrap: break-word;
  
  &:focus {
    outline: none;
    border-color: #4A90E2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
`;

const Button = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &.primary {
    background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
    color: white;
  }
  
  &.secondary {
    background: #f5f5f5;
    color: #666;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const FeedbackModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  feedbackText, 
  setFeedbackText, 
  stage, 
  isSubmitting = false,
  setRequestingChanges,
  reloadContent
}) => {
  const [localFeedback, setLocalFeedback] = useState(feedbackText || '');

  useEffect(() => {
    if (isOpen) {
      setLocalFeedback(feedbackText || '');
    }
  }, [isOpen, feedbackText]);

  const handleSubmit = async () => {
    
    if (!localFeedback.trim()) {
      return;
    }
    
    // 1. Set feedback text
    setFeedbackText(localFeedback);
    
    // 2. Close modal immediately
    onClose();
    
    // 3. Use the provided onSubmit function if available, otherwise use direct API call
    if (onSubmit) {
      try {
        await onSubmit(localFeedback);
      } catch (error) {
        console.error('onSubmit failed:', error);
      }
    } else {
    
    try {
      
      // Get sessionId from localStorage (same as TEST API)
      const sessionId = localStorage.getItem('workflowSessionId') || 'test-session-123';
      
      
      const response = await workflowAPI.processChanges(sessionId, stage, localFeedback);
      
      // Reload content to get the revised version
      
      // First, let's check what the server returns
      try {
        const updatedContent = await workflowAPI.getCurrentStage(sessionId);
        
        // Content received successfully
        const stageContent = updatedContent?.data?.[stage] || updatedContent?.[stage] || `No ${stage} content found`;
        
        // Since content is received but not displayed, force a page reload
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (reloadError) {
        console.error('Error reloading content:', reloadError);
      }
      
    } catch (error) {
      console.error('Error in feedback API call:', error);
      console.error('Error details:', error.message, error.stack);
    }
    }
    
  };

  const handleClose = () => {
    setLocalFeedback('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <ModalContainer
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton
              onClick={handleClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={20} />
            </CloseButton>

            <ModalTitle>
              Request Changes - {stage?.charAt(0).toUpperCase() + stage?.slice(1)}
            </ModalTitle>
            
            <ModalSubtitle>
              Please provide specific feedback about what you'd like to change in the {stage} content.
              Be as detailed as possible to help us improve the content.
            </ModalSubtitle>

            <TextArea
              value={localFeedback}
              onChange={(e) => setLocalFeedback(e.target.value)}
              placeholder="Enter your feedback here..."
              disabled={isSubmitting}
            />

            <ButtonContainer>
              <Button
                className="secondary"
                onClick={handleClose}
                disabled={isSubmitting}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Cancel
              </Button>
              
              <Button
                className="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !localFeedback.trim()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Send size={16} />
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </ButtonContainer>
          </ModalContainer>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default FeedbackModal;